import { Component, Injector, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AlertController, ModalController } from '@ionic/angular';
import { ChangePasswordRequest } from '@timechef/core/api';
import { AuthRepository, AuthService } from '@timechef/core/auth';
import { I18nService } from '@timechef/core/i18n/i18n.service';
import { AppRequestStatus } from '@timechef/core/utils/elf-utils';
import { EnforceFormGroupType } from '@timechef/core/utils/form-utils';

@Component({
  selector: 'app-change-password-modal',
  templateUrl: './change-password-modal.component.html',
  styleUrls: ['./change-password-modal.component.scss'],
  standalone: false,
})
export class ChangePasswordModalComponent implements OnInit {
  @Input() updateHasChanges?: (value: boolean) => void;

  oldPasswordControl = this.fb.nonNullable.control<string>('', { validators: Validators.required });
  newPasswordControl = this.fb.nonNullable.control<string>('', { validators: Validators.required });
  emailControl = this.fb.nonNullable.control('');

  changePasswordRequestStatus: AppRequestStatus = { value: 'idle' };

  form: EnforceFormGroupType<ChangePasswordRequest & { email: string }> = this.fb.nonNullable.group({
    email: this.emailControl,
    oldPassword: this.oldPasswordControl,
    newPassword: this.newPasswordControl,
  });

  private authService: AuthService;
  private authRepository: AuthRepository;

  constructor(
    private modalCtrl: ModalController,
    private fb: FormBuilder,
    injector: Injector,
    private alertCtrl: AlertController,
    private i18n: I18nService,
  ) {
    this.authService = injector.get(AuthService);
    this.authRepository = injector.get(AuthRepository);
  }

  ngOnInit(): void {
    this.emailControl.setValue(this.authRepository.getUserEmail());
    this.authRepository.changePasswordRequestStatus$.subscribe((status) => {
      this.changePasswordRequestStatus = status;
    });

    if (this.updateHasChanges) this.form.valueChanges.subscribe((_value) => this.updateHasChanges!(this.form.dirty));
  }

  async dismissModal() {
    this.modalCtrl.dismiss();
  }

  onSubmit() {
    if (this.form.invalid || this.changePasswordRequestStatus.value === 'pending') return;

    this.authService.changePassword(this.form.getRawValue()).subscribe(async (_tokenResult) => {
      const alert = await this.alertCtrl.create({
        message: this.i18n.translate('ACCOUNT_CHANGE_PASSWORD_MODAL_COMPONENT.SUCCESS'),
        buttons: [this.i18n.translate('ACCOUNT_CHANGE_PASSWORD_MODAL_COMPONENT.OK')],
      });
      await alert.present();
      if (await alert.onWillDismiss()) {
        this.form.markAsPristine();
        if (this.updateHasChanges) this.updateHasChanges(false);
        this.modalCtrl.dismiss();
      }
    });
  }
}
